import { applicantSection, otherAttachments, replySection } from "modules/ebiForms/common";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * B10. Skargi, wnioski i petycje — KPA dział VIII (art. 221–259) i ustawa z 11.07.2014 r. o petycjach. Brak wzorów
 * ogólnopolskich; układ jak formularze Gliwic (BPM.2 — skarga, BPM.1 — petycja). Rodzaj pisma jest wariantem wniosku
 * w BE (tytuł przesyłki: Skarga / Wniosek / Petycja).
 */
const COMPLAINT: EbiCondition = { field: "rodzajPisma", equals: "skarga" };
const MOTION: EbiCondition = { field: "rodzajPisma", equals: "wniosek" };
const PETITION: EbiCondition = { field: "rodzajPisma", equals: "petycja" };
const KPA: EbiCondition = { any: [COMPLAINT, MOTION] };
const THIRD_PARTY: EbiCondition = { all: [PETITION, { field: "petycjaInteres", equals: "trzeciego" }] };
const OTHER_PERSON: EbiCondition = { all: [KPA, { field: "wInteresieInnejOsoby", equals: true }] };

const B10: IEbiFormDefinition = {
  code: "B10",
  version: 1,
  title: "Skarga, wniosek lub petycja",
  category: EEbiFormCategory.General,
  office: EEbiFormOffice.Municipality,
  description: "Złóż skargę na działanie urzędu, wniosek w sprawie jego pracy (KPA) albo petycję.",
  keywords: ["skarga", "wniosek", "petycja", "kpa", "pismo"],
  organ: "Wójt / Burmistrz / Prezydent Miasta albo rada gminy — zależnie od przedmiotu pisma",
  legalBasis:
    "art. 221–259 ustawy z dnia 14 czerwca 1960 r. — Kodeks postępowania administracyjnego; ustawa z dnia 11 lipca 2014 r. o petycjach",
  feeLabel: "bez opłaty",
  fee: "Bez opłaty.",
  deadlineLabel: "do miesiąca",
  deadline: "Skarga i wniosek — do miesiąca (art. 237 i 244 KPA), petycja — do 3 miesięcy (art. 10 ustawy o petycjach).",
  documentType: "Pismo",
  variantField: "rodzajPisma",
  variants: {
    skarga: { title: "Skarga", documentType: "Skarga" },
    wniosek: { title: "Wniosek (art. 241 KPA)", documentType: "Wniosek" },
    petycja: { title: "Petycja", documentType: "Pismo" },
  },
  sections: [
    {
      code: "rodzaj",
      title: "Rodzaj pisma",
      fields: [
        {
          code: "rodzajPisma",
          type: "radio",
          label: "Składam",
          required: true,
          options: [
            { value: "skarga", label: "skargę (art. 227 KPA) — na zaniedbanie lub nienależyte wykonywanie zadań, przewlekłość, naruszenie praworządności" },
            { value: "wniosek", label: "wniosek (art. 241 KPA) — w sprawie ulepszenia organizacji, usprawnienia pracy, zaspokajania potrzeb mieszkańców" },
            { value: "petycja", label: "petycję (ustawa o petycjach) — żądanie zmiany przepisów, podjęcia rozstrzygnięcia lub innego działania" },
          ],
        },
      ],
    },
    applicantSection("Wnoszący"),
    {
      code: "skarga",
      title: "Skarga dotyczy",
      visibleWhen: COMPLAINT,
      fields: [
        {
          code: "skargaDotyczy",
          type: "checkboxes",
          label: "Skarga dotyczy",
          required: true,
          options: [
            { value: "zaniedbanie", label: "zaniedbania lub nienależytego wykonywania zadań przez organy albo ich pracowników" },
            { value: "praworzadnosc", label: "naruszenia praworządności lub interesów skarżących" },
            { value: "przewleklosc", label: "przewlekłego lub biurokratycznego załatwiania spraw" },
            { value: "inne", label: "inne" },
          ],
        },
        {
          code: "skargaDotyczyInne",
          type: "text",
          label: "Czego dotyczy skarga (inne)",
          required: true,
          visibleWhen: { field: "skargaDotyczy", includes: "inne" },
          maxLength: 300,
        },
        { code: "skargaNaKogo", type: "text", label: "Kogo dotyczy skarga (komórka urzędu, pracownik)", maxLength: 300 },
      ],
    },
    {
      code: "wniosekKpa",
      title: "Wniosek dotyczy",
      visibleWhen: MOTION,
      fields: [
        {
          code: "wniosekDotyczy",
          type: "checkboxes",
          label: "Przedmiot wniosku (art. 241 KPA)",
          options: [
            { value: "organizacja", label: "ulepszenia organizacji" },
            { value: "praworzadnosc", label: "wzmocnienia praworządności" },
            { value: "usprawnienie", label: "usprawnienia pracy i zapobiegania nadużyciom" },
            { value: "wlasnosc", label: "ochrony własności" },
            { value: "potrzeby", label: "lepszego zaspokajania potrzeb ludności" },
          ],
        },
      ],
    },
    {
      code: "innaOsoba",
      title: "W czyim interesie",
      visibleWhen: KPA,
      fields: [
        {
          code: "wInteresieInnejOsoby",
          type: "checkbox",
          label: "Składam pismo w interesie innej osoby, za jej zgodą (art. 221 § 3 KPA)",
        },
        { code: "innaOsobaNazwa", type: "text", label: "Imię i nazwisko albo nazwa tej osoby", required: true, visibleWhen: OTHER_PERSON, maxLength: 300 },
      ],
    },
    {
      code: "petycja",
      title: "Petycja",
      visibleWhen: PETITION,
      fields: [
        {
          code: "adresatPetycji",
          type: "text",
          label: "Adresat petycji",
          required: true,
          placeholder: "np. Wójt Gminy, Rada Gminy",
          maxLength: 300,
        },
        {
          code: "petycjaInteres",
          type: "radio",
          label: "Petycja składana w interesie",
          required: true,
          options: [
            { value: "publicznym", label: "publicznym" },
            { value: "wnoszacego", label: "podmiotu wnoszącego petycję" },
            { value: "trzeciego", label: "podmiotu trzeciego, za jego zgodą" },
          ],
        },
        {
          code: "petycjaZadanie",
          type: "checkboxes",
          label: "Żądanie",
          required: true,
          options: [
            { value: "przepisy", label: "zmiany przepisów prawa" },
            { value: "rozstrzygniecie", label: "podjęcia rozstrzygnięcia" },
            { value: "dzialanie", label: "podjęcia innego działania" },
          ],
        },
        {
          code: "petycjaSprawa",
          type: "checkboxes",
          label: "W sprawie dotyczącej",
          options: [
            { value: "wnoszacego", label: "podmiotu wnoszącego petycję" },
            { value: "zycieZbiorowe", label: "życia zbiorowego" },
            { value: "dobroWspolne", label: "wartości wymagających szczególnej ochrony w imię dobra wspólnego" },
          ],
        },
        { code: "podmiotTrzeciNazwa", type: "text", label: "Imię i nazwisko albo nazwa podmiotu trzeciego", required: true, visibleWhen: THIRD_PARTY, maxLength: 300 },
        { code: "podmiotTrzeciAdres", type: "text", label: "Miejsce zamieszkania albo siedziba podmiotu trzeciego", required: true, visibleWhen: THIRD_PARTY, maxLength: 300 },
        { code: "podmiotTrzeciKontakt", type: "text", label: "Adres do korespondencji lub e-mail podmiotu trzeciego", visibleWhen: THIRD_PARTY, maxLength: 300 },
        {
          code: "petycjaZgodaPublikacja",
          type: "declaration",
          label:
            "Zgodnie z art. 4 ust. 3 ustawy z dnia 11 lipca 2014 r. o petycjach wyrażam zgodę na ujawnienie na stronie internetowej urzędu lub w Biuletynie Informacji Publicznej moich danych osobowych zawartych w petycji (nieobowiązkowo — bez zgody petycja zostanie opublikowana bez danych).",
        },
      ],
    },
    {
      code: "tresc",
      title: "Treść pisma",
      fields: [
        { code: "znakSprawy", type: "text", label: "Znak sprawy, której dotyczy pismo", hint: "Jeśli pismo dotyczy konkretnej sprawy w urzędzie.", maxLength: 100 },
        { code: "tresc", type: "textarea", label: "Treść", required: true, maxLength: 20000 },
      ],
    },
    replySection(),
  ],
  attachments: [
    {
      code: "zgodaInnejOsoby",
      label: "Zgoda osoby, w której interesie składane jest pismo",
      visibleWhen: OTHER_PERSON,
      required: true,
    },
    {
      code: "zgodaPodmiotuTrzeciego",
      label: "Zgoda podmiotu trzeciego na złożenie petycji w jego interesie",
      hint: "Art. 5 ustawy o petycjach.",
      visibleWhen: THIRD_PARTY,
      required: true,
    },
    {
      code: "dokumentySprawy",
      label: "Kopie pism, decyzji i dowodów w sprawie",
      multiple: true,
      visibleWhen: { field: "rodzajPisma", filled: true },
    },
    otherAttachments(),
  ],
};

export default B10;
